import { useId, useMemo, useState } from "react";

import type { AccessScope } from "@/lib/api/access-requests";
import type { LiveCatalogSelection } from "@/lib/api/live-catalog";
import {
  MIN_CATALOG_SEARCH_LENGTH,
  useLiveCatalogSearch,
} from "@/pages/glofox/useLiveCatalogSearch";

export function LiveScopePicker({
  mode,
  selected,
  onChange,
  testId,
}: {
  mode: AccessScope;
  selected: LiveCatalogSelection[];
  onChange(next: LiveCatalogSelection[]): void;
  testId: string;
}) {
  const labelId = useId();
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState(mode);
  // A different access scope is a new search. Drop the previous text before paint.
  if (searchMode !== mode) {
    setSearchMode(mode);
    setQuery("");
  }
  const label = mode === "namespaces" ? "Namespaces" : "Locations";
  const placeholder =
    mode === "namespaces"
      ? "Search by business name or namespace"
      : "Search by name, id, or namespace";
  const search = useLiveCatalogSearch(query, mode);

  const options = useMemo(() => {
    if (mode === "locations") {
      return search.locations.map((item) => ({
        id: item.id,
        name: item.name,
        namespace: item.namespace,
      }));
    }
    return search.namespaces.map((item) => ({
      id: item.namespace,
      name: item.name,
      namespace: item.namespace,
    }));
  }, [mode, search.locations, search.namespaces]);

  const selectedIds = new Set(selected.map((item) => item.id));
  const searchActive = [...query.trim()].length >= MIN_CATALOG_SEARCH_LENGTH;

  function toggle(option: LiveCatalogSelection) {
    const selection = {
      id: option.id,
      name: option.name,
      namespace: option.namespace,
    };
    if (selectedIds.has(selection.id)) {
      onChange(selected.filter((item) => item.id !== selection.id));
      return;
    }
    onChange([...selected, selection]);
  }

  let status: string | null = null;
  if (!searchActive) {
    status = "Type at least 3 characters.";
  } else if (search.loading) {
    status =
      mode === "locations" ? "Loading locations…" : "Loading namespaces…";
  } else if (!search.error && options.length === 0) {
    status = "No matches.";
  }

  return (
    <div className="dev-scope-picker" data-testid={testId}>
      <span id={labelId}>{label}</span>
      {selected.length > 0 ? (
        <ul className="dev-scope-picker__chips">
          {selected.map((item) => (
            <li key={item.id}>
              <span>{item.name}</span>
              {mode === "namespaces" ? (
                <small className="dev-scope-picker__id">{item.namespace}</small>
              ) : null}
              <button
                type="button"
                data-testid={`${testId}-chip-${item.id}`}
                aria-label={`Remove ${item.name}`}
                onClick={() =>
                  onChange(selected.filter((current) => current.id !== item.id))
                }
              >
                <i className="ti ti-x" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        type="search"
        aria-labelledby={labelId}
        data-testid={`${testId}-search`}
        value={query}
        placeholder={placeholder}
        onChange={(event) => setQuery(event.target.value)}
      />
      {search.error ? (
        <p className="dev-form__error" role="alert">
          {search.error}
        </p>
      ) : status ? (
        <p className="dev-scope-picker__status">{status}</p>
      ) : (
        <ul
          className="dev-scope-picker__list"
          role="listbox"
          aria-multiselectable="true"
          aria-label={`${label} list`}
        >
          {options.map((option) => {
            const isSelected = selectedIds.has(option.id);
            return (
              <li key={option.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`${testId}-option-${option.id}`}
                  className={isSelected ? "is-selected" : undefined}
                  onClick={() => toggle(option)}
                >
                  <strong>{option.name}</strong>
                  <small>{option.namespace}</small>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
